import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { HiOutlineEyeSlash } from 'react-icons/hi2';
import jobListingsService from '../../services/JobListingsService';

/**
 * Confirms taking a job off the website. Nothing is deleted: the job stays in
 * the CMS and on this page as a draft, and "Veröffentlichen" puts it back.
 */
export function DeactivateJobModal({ job, onClose, onDeactivated }) {
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === 'Escape' && !saving) onClose();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [onClose, saving]);

  async function deactivate() {
    setSaving(true);
    const result = await jobListingsService.unpublishJob(job.id);
    setSaving(false);

    if (!result.success) {
      toast.error(result.error);
      return;
    }
    toast.success(`„${job.title}“ ist deaktiviert`);
    onDeactivated?.();
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs"
      onClick={saving ? undefined : onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="deactivate-job-title"
        onClick={(event) => event.stopPropagation()}
        className="w-full max-w-md rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 shadow-2xl"
      >
        <div className="flex items-start gap-4">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400">
            <HiOutlineEyeSlash className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h2 id="deactivate-job-title" className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              Stelle deaktivieren?
            </h2>
            <p className="mt-0.5 text-sm font-medium text-slate-700 dark:text-slate-300">{job.title}</p>
            <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">
              Die Stellenanzeige erscheint danach nicht mehr auf der Website. Mit „Veröffentlichen“ ist sie jederzeit
              wieder online.
            </p>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            autoFocus
            onClick={onClose}
            disabled={saving}
            className="rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4 py-2 text-sm font-semibold text-slate-700 dark:text-slate-200 transition-colors hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={deactivate}
            disabled={saving}
            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-60 disabled:cursor-wait focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900"
          >
            {saving ? 'Wird deaktiviert …' : 'Deaktivieren'}
          </button>
        </div>
      </div>
    </div>
  );
}
